import React, { useState } from 'react';
import { ChevronDown } from '../../../../assets/icons/ChevronDown';
import { CircleCrossSmallIcon } from '../../../../assets/icons/CircleCrossSmallIcon';
import { CircleMinusSmallIcon } from '../../../../assets/icons/CircleMinusSmallIcon';
import { CircleTickFilledIcon } from '../../../../assets/icons/CircleTickFilledIcon';

export interface AccordionPanel {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  subtitle?: string;
  currentWarranty?: 'covered' | 'notCovered' | 'partial';
  additionalContent?: string;
}

export interface ProtectionDetailsAccordionPanelWibProps {
  panels?: AccordionPanel[];
  isDesktop?: boolean;
  warrantyLength?: number;
  currentWarrantyLabel?: string;
  quoteCoverageLabel?: string;
}

const styles = {
  headingLabels: 'flex justify-between items-end w-35 tablet:w-40.5',
  headingLabel:
    'flex flex-col items-center text-body-small-strong text-text-body w-17.5 tablet:w-18',
  row: 'flex flex-col w-full cursor-pointer pr-space-600',
  rowDivider: 'border-t border-card-border-neutral pt-space-300',
  columns: 'flex justify-between w-23.5 shrink-0 tablet:w-28.5',
  column: 'w-6 flex flex-col items-center gap-1.5',
  columnCaption: 'w-20 text-body-small text-text-body text-center tablet:w-20.5',
  mutedIcon: 'text-text-placeholder',
};

export function BenefitsTableAccordion({
  panels = [],
  warrantyLength,
}: ProtectionDetailsAccordionPanelWibProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const togglePanel = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  const warrantyInYears = warrantyLength ? Math.floor(warrantyLength / 12) : 0;

  const currentWarrantyCell = (panel: AccordionPanel) => {
    if (panel.currentWarranty === 'covered') {
      return (
        <span className={styles.column}>
          <CircleTickFilledIcon />
          {warrantyInYears > 0 && (
            <p className="w-11 text-center text-body-small text-text-body tablet:w-auto tablet:text-nowrap">
              {warrantyInYears} year{warrantyInYears > 1 ? 's' : ''} only
            </p>
          )}
        </span>
      );
    }

    return (
      <span className={styles.column}>
        <span className={styles.mutedIcon}>
          {panel.currentWarranty === 'partial' ? (
            <CircleMinusSmallIcon />
          ) : (
            <CircleCrossSmallIcon />
          )}
        </span>
        {panel.additionalContent && (
          <p className={styles.columnCaption}>{panel.additionalContent}</p>
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-space-400">
      <div className="flex justify-end">
        <div className={styles.headingLabels}>
          <h5 className={styles.headingLabel}>
            <span>Manufacturer</span>
            <span>Warranty</span>
          </h5>
          <h5 className={styles.headingLabel}>
            <span>Extra</span>
            <span>Coverage</span>
          </h5>
        </div>
      </div>

      <div className="flex flex-col gap-space-300 pt-space-200">
        {panels.map((panel, index) => {
          const expanded = expandedIndex === index;
          return (
            <div
              key={index}
              className={`${styles.row} ${index > 0 ? styles.rowDivider : ''}`}
              onClick={() => togglePanel(index)}
              role="button"
              aria-expanded={expanded}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  togglePanel(index);
                }
              }}
            >
              <div className="flex flex-row items-start gap-space-300 w-full">
                <div className="flex flex-row items-start gap-space-200 flex-1">
                  <span className="w-4 h-6 flex items-center justify-center shrink-0">
                    <ChevronDown isOpen={expanded} className="text-text-body" />
                  </span>
                  <div className="flex flex-col gap-space-200 flex-1">
                    <p className="min-h-6 flex items-center text-left text-body-strong text-text-body wrap-break-word">
                      {panel.title}
                    </p>
                    {expanded && panel.description && (
                      <p
                        // Keeps clear of the caption that overflows the icon column.
                        className={`text-left text-body text-text-body ${panel.additionalContent ? 'pr-6' : ''}`}
                      >
                        {panel.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className={styles.columns}>
                  {currentWarrantyCell(panel)}
                  <span className={styles.column}>
                    <CircleTickFilledIcon />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
